import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

const BATCH_NAME_PLACEHOLDERS = [
  'Can we get Sukhothai?',
  'Did you do an EKG?',
  'Can you check in the new patient?',
  'Does she ever get off laptop?',
];
const RANDOM_PLACEHOLDER = BATCH_NAME_PLACEHOLDERS[Math.floor(Math.random() * BATCH_NAME_PLACEHOLDERS.length)];

const UploadIcon = () => (
  <svg className="drop-zone-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

const FileIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
  </svg>
);

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const THEME = {
  full:      { color: '#2563eb', light: '#dbeafe', text: '#1e40af' },
  name_only: { color: '#059669', light: '#d1fae5', text: '#065f46' },
};

export default function Upload({ mode = 'full' }) {
  const theme = THEME[mode] || THEME.full;
  const { authHeader } = useAuth();
  const [file, setFile] = useState(null);
  const [batchName, setBatchName] = useState('');
  const [doubleSided, setDoubleSided] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null); // { current, total }
  const [uploadProgress, setUploadProgress] = useState(null); // { loaded, total } bytes
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [dragover, setDragover] = useState(false);
  const inputRef = useRef();
  const pollRef = useRef(null);

  const now = new Date();
  const [selectedMonths, setSelectedMonths] = useState([now.getMonth() + 1]);
  const [batchYear, setBatchYear] = useState(now.getFullYear());
  const [doctorList, setDoctorList] = useState([]);
  const [selectedDoctorIds, setSelectedDoctorIds] = useState([]);
  const [showDoctorWarning, setShowDoctorWarning] = useState(false);

  // Fetch doctor list on mount (full mode only)
  useEffect(() => {
    if (mode !== 'full') return;
    authHeader().then(headers =>
      axios.get(`${API}/doctors/`, { headers })
        .then(res => setDoctorList([...res.data].sort((a, b) => a.name.localeCompare(b.name))))
        .catch(() => {})
    );
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clean up polling on unmount
  useEffect(() => () => clearInterval(pollRef.current), []);

  const handleFile = (f) => {
    if (!f) return;
    if (f.type !== 'application/pdf') {
      setError('Please select a PDF file.');
      return;
    }
    setFile(f);
    setError('');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragover(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const doUpload = async () => {
    setShowDoctorWarning(false);
    setLoading(true);
    setError('');
    setResult(null);
    setProgress(null);
    setUploadProgress(null);

    const startPolling = (batchId) => {
      pollRef.current = setInterval(async () => {
        try {
          const prog = await axios.get(`${API}/batches/${batchId}/progress/`, {
            headers: await authHeader(),
          });
          const { status, current_page, total_pages } = prog.data;
          setProgress({ current: current_page, total: total_pages });

          if (status === 'complete') {
            clearInterval(pollRef.current);
            const detail = await axios.get(`${API}/batches/${batchId}/`, {
              headers: await authHeader(),
            });
            const b = detail.data;
            const rows = [];
            for (const p of b.patients) {
              if (p.visits?.length) {
                for (const v of p.visits) {
                  rows.push({ ...p, date_of_service: v });
                }
              } else {
                rows.push({ ...p, date_of_service: 'Unknown' });
              }
            }
            setResult({
              batch_id: b.id,
              total_patients: b.patients.length,
              needs_review: b.patients.filter(p => p.needs_review).length,
              patients: rows,
              sorted_pdf_url: b.sorted_pdf_url,
              excel_url: b.excel_url,
            });
            setLoading(false);
          } else if (status === 'error') {
            clearInterval(pollRef.current);
            const msg = prog.data.error_message;
            setError(msg ? `Processing failed: ${msg}` : 'Processing failed. Please try again.');

            // Fetch partial results — if any pages completed, show what we have
            try {
              const detail = await axios.get(`${API}/batches/${batchId}/`, {
                headers: await authHeader(),
              });
              const b = detail.data;
              if (b.patients?.length > 0) {
                const rows = [];
                for (const p of b.patients) {
                  if (p.visits?.length) {
                    for (const v of p.visits) {
                      rows.push({ ...p, date_of_service: v });
                    }
                  } else {
                    rows.push({ ...p, date_of_service: 'Unknown' });
                  }
                }
                setResult({
                  batch_id: b.id,
                  total_patients: b.patients.length,
                  needs_review: b.patients.filter(p => p.needs_review).length,
                  patients: rows,
                  sorted_pdf_url: b.sorted_pdf_url,
                  excel_url: b.excel_url,
                  partial: true,
                });
              }
            } catch {
              // partial fetch failed — error message is already shown
            }

            setLoading(false);
          }
        } catch {
          // polling error — keep trying
        }
      }, 1500);
    };

    try {
      const headers = await authHeader();

      // ── Upload in 5 MB chunks directly to Railway ─────────────────────────
      const CHUNK_SIZE = 5 * 1024 * 1024;
      const MAX_RETRIES = 3;
      const sessionId = crypto.randomUUID();
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      let bytesUploaded = 0;
      let batchRes;

      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const chunkBase = bytesUploaded;
        const isLast = i === totalChunks - 1;

        let lastErr;
        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
          // Exponential backoff before each retry: 1s, 2s, 4s
          if (attempt > 0) {
            await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt - 1)));
          }

          // Rebuild FormData fresh each attempt (avoids consumed-stream issues)
          const chunkForm = new FormData();
          chunkForm.append('session_id', sessionId);
          chunkForm.append('chunk_index', String(i));
          chunkForm.append('total_chunks', String(totalChunks));
          chunkForm.append('chunk', chunk, 'chunk.bin');
          if (isLast) {
            chunkForm.append('double_sided', doubleSided.toString());
            chunkForm.append('batch_name', batchName.trim());
            chunkForm.append('mode', mode);
            chunkForm.append('batch_months', JSON.stringify(selectedMonths));
            chunkForm.append('batch_year', String(batchYear));
            chunkForm.append('batch_doctor_ids', JSON.stringify(selectedDoctorIds));
          }

          try {
            const res = await axios.post(`${API}/upload-chunk/`, chunkForm, {
              headers: { ...headers, 'Content-Type': 'multipart/form-data' },
              onUploadProgress: (e) => {
                if (e.total) {
                  setUploadProgress({ loaded: chunkBase + Math.min(e.loaded, e.total), total: file.size });
                }
              },
            });
            if (isLast) batchRes = res;
            lastErr = null;
            break;
          } catch (err) {
            lastErr = err;
          }
        }

        if (lastErr) throw lastErr;

        bytesUploaded = end;
        setUploadProgress({ loaded: bytesUploaded, total: file.size });
      }

      startPolling(batchRes.data.batch_id);

    } catch (err) {
      let msg;
      if (err.code === 'ECONNABORTED' || err.message?.toLowerCase().includes('timeout')) {
        msg = 'Upload timed out. The file may be too large or your connection is slow.';
      } else if (!err.response) {
        msg = `Network error: could not reach the server. (${err.message || 'unknown'})`;
      } else if (err.response.status === 413) {
        msg = 'File too large for the server to accept.';
      } else {
        const errData = err.response?.data;
        const extracted = errData?.error?.message || errData?.error || errData?.detail;
        msg = (typeof extracted === 'string' ? extracted : null)
          || `Upload failed (HTTP ${err.response?.status}).`;
      }
      console.error('Upload error:', err);
      setError(msg);
      setLoading(false);
    }
  };

  const handleUpload = () => {
    if (!file) return;
    if (mode === 'full' && doctorList.length > 0 && selectedDoctorIds.length === 0) {
      setShowDoctorWarning(true);
      return;
    }
    doUpload();
  };

  const reset = () => {
    clearInterval(pollRef.current);
    setFile(null);
    setBatchName('');
    setResult(null);
    setProgress(null);
    setUploadProgress(null);
    setError('');
    setSelectedMonths([new Date().getMonth() + 1]);
    setBatchYear(new Date().getFullYear());
    setSelectedDoctorIds([]);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div>
      {/* Upload card */}
      <div className="card" style={{ marginBottom: '1.25rem', overflow: 'hidden' }}>
        <div style={{ height: 4, background: theme.color }} />
        <div className="card-header">
          <span className="card-title">New Batch</span>
          {result && (
            <button className="btn btn-secondary btn-sm" onClick={reset}>
              Upload Another
            </button>
          )}
        </div>
        <div className="card-body">
          {error && <div className="alert alert-error">{error}</div>}

          {!result && (
            <>
              {/* Batch name */}
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label">Batch Name <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(optional)</span></label>
                <input
                  type="text"
                  className="form-input"
                  placeholder={RANDOM_PLACEHOLDER}
                  value={batchName}
                  onChange={e => setBatchName(e.target.value)}
                />
              </div>

              {mode === 'full' && (
                <>
                  {/* Month + Year */}
                  <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <label className="form-label">
                      Months of Service
                      <span style={{ color: 'var(--text-3)', fontWeight: 400, marginLeft: 6 }}>(select all that apply)</span>
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '0.35rem', marginBottom: '0.75rem' }}>
                      {MONTHS.map((name, idx) => {
                        const num = idx + 1;
                        const checked = selectedMonths.includes(num);
                        return (
                          <label key={num} style={{
                            display: 'flex', alignItems: 'center', gap: '0.3rem',
                            padding: '0.3rem 0.5rem', borderRadius: 6, cursor: 'pointer',
                            background: checked ? theme.light : 'var(--bg-2)',
                            border: `1px solid ${checked ? theme.color : 'var(--border)'}`,
                            fontSize: '0.8rem', fontWeight: checked ? 600 : 400,
                            color: checked ? theme.text : 'var(--text-2)',
                            userSelect: 'none',
                          }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              style={{ display: 'none' }}
                              onChange={() => setSelectedMonths(prev =>
                                prev.includes(num) ? prev.filter(m => m !== num) : [...prev, num].sort((a, b) => a - b)
                              )}
                            />
                            {name}
                          </label>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <label className="form-label" style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>Year</label>
                      <input
                        type="number"
                        className="form-input"
                        style={{ width: 100 }}
                        value={batchYear}
                        min={2020}
                        max={2099}
                        onChange={e => setBatchYear(Number(e.target.value))}
                      />
                    </div>
                  </div>

                  {/* Doctor selector */}
                  {doctorList.length > 0 && (
                    <div className="form-group" style={{ marginBottom: '1rem' }}>
                      <label className="form-label">
                        Doctors in This Batch
                        <span style={{ color: 'var(--error, #e53e3e)', marginLeft: 4 }}>*</span>
                        {selectedDoctorIds.length > 0 && (
                          <span style={{ color: 'var(--text-3)', fontWeight: 400, marginLeft: 6 }}>
                            ({selectedDoctorIds.length} selected)
                          </span>
                        )}
                      </label>
                      <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: '0.35rem',
                        padding: '0.5rem', borderRadius: 8,
                        border: `1px solid ${selectedDoctorIds.length === 0 ? 'var(--error, #e53e3e)' : 'var(--border)'}`,
                        background: 'var(--bg-2)',
                        maxHeight: 160, overflowY: 'auto',
                      }}>
                        {doctorList.map(doc => {
                          const sel = selectedDoctorIds.includes(doc.id);
                          return (
                            <button
                              key={doc.id}
                              type="button"
                              onClick={() => setSelectedDoctorIds(prev =>
                                prev.includes(doc.id) ? prev.filter(id => id !== doc.id) : [...prev, doc.id]
                              )}
                              style={{
                                padding: '0.25rem 0.65rem', borderRadius: 99, cursor: 'pointer',
                                background: sel ? theme.color : 'var(--bg)',
                                border: `1px solid ${sel ? theme.color : 'var(--border)'}`,
                                color: sel ? '#fff' : 'var(--text-2)',
                                fontSize: '0.8rem', fontWeight: sel ? 600 : 400,
                              }}
                            >
                              {doc.name}
                            </button>
                          );
                        })}
                      </div>
                      {selectedDoctorIds.length === 0 && (
                        <div style={{ fontSize: '0.78rem', color: 'var(--error, #e53e3e)', marginTop: '0.3rem' }}>
                          Select the doctors who will appear in this batch.
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Drop zone */}
              <div
                className={`drop-zone${dragover ? ' dragover' : ''}`}
                style={{ marginBottom: '1.25rem' }}
                onDragOver={e => { e.preventDefault(); setDragover(true); }}
                onDragLeave={() => setDragover(false)}
                onDrop={handleDrop}
                onClick={() => !file && inputRef.current?.click()}
              >
                <input
                  ref={inputRef}
                  type="file"
                  accept=".pdf"
                  onChange={e => handleFile(e.target.files[0])}
                  style={{ display: 'none' }}
                />
                <UploadIcon />
                {file ? (
                  <div className="drop-zone-file-name">
                    <FileIcon />
                    {file.name}
                  </div>
                ) : (
                  <>
                    <div className="drop-zone-text">Drop PDF here or click to browse</div>
                    <div className="drop-zone-hint">Accepts .pdf files only</div>
                  </>
                )}
              </div>

              {file && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={e => { e.stopPropagation(); reset(); }}
                  style={{ marginBottom: '1rem', marginTop: '-0.5rem' }}
                >
                  × Clear file
                </button>
              )}

              {/* Options */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label className="form-check">
                  <input
                    type="checkbox"
                    checked={doubleSided}
                    onChange={e => setDoubleSided(e.target.checked)}
                  />
                  <span className="form-check-label">Double-sided face sheets (2 pages per patient)</span>
                </label>
              </div>

              <button
                className="btn btn-primary btn-lg"
                onClick={handleUpload}
                disabled={!file || loading}
                style={{ background: theme.color, borderColor: theme.color }}
              >
                {loading ? (
                  <>
                    <span className="spinner" />
                    Processing…
                  </>
                ) : (
                  'Upload & Process Batch'
                )}
              </button>
            </>
          )}

          {loading && (
            <div className="processing-card">
              <div className="processing-spinner" />
              <div className="processing-title">Processing your batch</div>
              {progress && progress.total > 0 ? (
                <>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.color, margin: '0.5rem 0' }}>
                    {progress.current} / {progress.total}
                  </div>
                  <div style={{ width: '100%', maxWidth: 280, background: 'var(--border)', borderRadius: 99, height: 6, margin: '0 auto 0.5rem' }}>
                    <div style={{
                      height: 6, borderRadius: 99,
                      background: theme.color,
                      width: `${Math.round((progress.current / progress.total) * 100)}%`,
                      transition: 'width 0.4s ease',
                    }} />
                  </div>
                  <div className="processing-subtitle">
                    Reading facesheet {progress.current} of {progress.total}
                  </div>
                </>
              ) : uploadProgress ? (
                <>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.color, margin: '0.5rem 0' }}>
                    {Math.round((uploadProgress.loaded / uploadProgress.total) * 100)}%
                  </div>
                  <div style={{ width: '100%', maxWidth: 280, background: 'var(--border)', borderRadius: 99, height: 6, margin: '0 auto 0.5rem' }}>
                    <div style={{
                      height: 6, borderRadius: 99,
                      background: theme.color,
                      width: `${Math.round((uploadProgress.loaded / uploadProgress.total) * 100)}%`,
                      transition: 'width 0.3s ease',
                    }} />
                  </div>
                  <div className="processing-subtitle">
                    Uploading… {(uploadProgress.loaded / 1024 / 1024).toFixed(1)} / {(uploadProgress.total / 1024 / 1024).toFixed(1)} MB
                  </div>
                </>
              ) : (
                <div className="processing-subtitle">Preparing upload…</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      {result && !loading && (
        <>
          <div className="card" style={{ marginBottom: '1.25rem', overflow: 'hidden' }}>
            <div style={{ height: 4, background: theme.color }} />
            <div className="card-header">
              <span className="card-title">{result.partial ? 'Partial Results' : 'Batch Complete'}</span>
              <span className={`badge ${result.partial ? 'badge-warning' : 'badge-success'}`}>Batch #{result.batch_id}</span>
            </div>
            <div className="card-body">
              <div className="stats-row">
                <div className="stat-chip primary">
                  <div className="stat-chip-value">{result.total_patients}</div>
                  <div className="stat-chip-label">Patients</div>
                </div>
                {result.needs_review > 0 && (
                  <div className="stat-chip warning">
                    <div className="stat-chip-value">{result.needs_review}</div>
                    <div className="stat-chip-label">Need Review</div>
                  </div>
                )}
                <div className="stat-chip success">
                  <div className="stat-chip-value">{result.total_patients - result.needs_review}</div>
                  <div className="stat-chip-label">Ready</div>
                </div>
              </div>

              <div className="download-row">
                <a href={result.sorted_pdf_url} target="_blank" rel="noreferrer">
                  <button className="btn btn-primary" style={{ background: theme.color, borderColor: theme.color }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    Download Sorted PDF
                  </button>
                </a>
                <a href={result.excel_url} target="_blank" rel="noreferrer">
                  <button className="btn btn-success">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    Download Excel
                  </button>
                </a>
              </div>
            </div>
          </div>

          <div className="card" style={{ overflow: 'hidden' }}>
            <div style={{ height: 4, background: theme.color }} />
            <div className="card-header">
              <span className="card-title">Patient Records: A to Z</span>
              {mode === 'full' && result.needs_review > 0 && (
                <span className="badge badge-warning">{result.needs_review} need review</span>
              )}
            </div>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Last Name</th>
                    <th>First Name</th>
                    {mode === 'full' && <th>Doctor Seen</th>}
                    {mode === 'full' && <th>Date of Service</th>}
                    {mode === 'full' && <th>Status</th>}
                  </tr>
                </thead>
                <tbody>
                  {result.patients.map((p, i) => {
                    return (
                      <tr key={`${p.id}-${i}`}>
                        <td style={{ color: 'var(--text-3)', width: 40 }}>{i + 1}</td>
                        <td style={{ fontWeight: 500 }}>{p.last_name}</td>
                        <td>{p.first_name}</td>
                        {mode === 'full' && <td style={{ color: 'var(--text-2)' }}>{p.doctor}</td>}
                        {mode === 'full' && <td style={{ color: 'var(--text-2)' }}>{p.date_of_service || 'N/A'}</td>}
                        {mode === 'full' && (
                          <td>
                            <span className={`badge ${p.needs_review ? 'badge-warning' : 'badge-success'}`}>
                              {p.needs_review ? 'Review' : 'Ready'}
                            </span>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── No-doctor warning modal ──────────────────────────────────── */}
      {showDoctorWarning && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div className="card" style={{ maxWidth: 420, width: '90%', margin: 0 }}>
            <div className="card-header">
              <span className="card-title">No Doctors Selected</span>
            </div>
            <div className="card-body">
              <p style={{ color: 'var(--text-2)', lineHeight: 1.65, marginBottom: '0.85rem' }}>
                Selecting which doctors appear in this batch helps the system read ambiguous handwriting more accurately and can significantly improve results.
              </p>
              <p style={{ color: 'var(--text-3)', fontSize: '0.85rem', lineHeight: 1.55, marginBottom: '1.5rem' }}>
                If you continue, the system will automatically scan a few pages first to discover which doctors are present before processing the rest.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={() => setShowDoctorWarning(false)}>
                  Go Back &amp; Select
                </button>
                <button className="btn btn-primary" onClick={doUpload}>
                  Continue Anyway
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
